import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import type { Aviation, Aircraft } from './aviation';
import type { Soldier } from './military';
import type { Vec } from '../destruction/structure';

interface Occupant {
  soldier: Soldier;
  aircraft: Aircraft;
  slot: number;
  local: THREE.Vector3;
  state: 'aboard' | 'leaving' | 'returning' | 'departed' | 'casualty';
  path: THREE.Vector3[];
  trail: THREE.Vector3[];
  landing?: Vec;
}
interface Drop {
  queue: Occupant[];
  next: number;
}
const UP = new THREE.Vector3(0, 1, 0);
/** Original troop identities occupy supported positions in the cargo hold.
 * A drop walks the cabin aisles and open rear hatch before creating a canopy.
 * Physics queries use the actual current carrier transform, independently of
 * the next interpolated render pose of a fast-moving aircraft. */
export class AirManifest {
  readonly people = new Map<string, Occupant>();
  private drops = new Map<Aircraft, Drop>();
  private exitClearanceUntil = new Map<Aircraft, number>();
  private capsule = new RAPIER.Capsule(0.58, 0.24);
  onJump: (
    soldier: Soldier,
    landing: Vec,
    velocity: Vec,
    clearanceSeconds: number,
    aircraft: Aircraft,
  ) => boolean = () => false;
  constructor(readonly aviation: Aviation) {}
  clear(): void {
    this.people.clear();
    this.drops.clear();
    this.exitClearanceUntil.clear();
  }
  slot(index: number): THREE.Vector3 {
    const col = index % 6;
    return new THREE.Vector3(
      col < 3 ? -0.9 - (2 - col) * 0.9 : 0.9 + (col - 3) * 0.9,
      3.95,
      -10 + Math.floor(index / 6) * 1.25,
    );
  }
  point(a: Aircraft, local: Vec, physical = false): THREE.Vector3 {
    const p = new THREE.Vector3(local.x, local.y, local.z);
    if (!physical) return p.applyQuaternion(a.q).add(a.p);
    return p
      .add(a.anchor)
      .sub(a.cluster.origin)
      .applyQuaternion(a.cluster.body.rotation())
      .add(a.cluster.body.translation());
  }
  support(a: Aircraft, local: Vec): number | undefined {
    if (!a.cluster.body.isValid()) return;
    const q = new THREE.Quaternion().copy(a.cluster.body.rotation()),
      from = this.point(a, { ...local, y: local.y + 0.12 }, true),
      dir = UP.clone().negate().applyQuaternion(q);
    let distance = Infinity;
    for (let i = 0; i < a.cluster.body.numColliders(); i++) {
      const hit = a.cluster.body.collider(i).castRayAndGetNormal(new RAPIER.Ray(from, dir), 0.4, false);
      if (hit && new THREE.Vector3().copy(hit.normal).applyQuaternion(q.clone().invert()).y > 0.65)
        distance = Math.min(distance, hit.timeOfImpact);
    }
    return Number.isFinite(distance) ? local.y + 0.12 - distance : undefined;
  }
  attach(a: Aircraft, soldier: Soldier, slot: number): boolean {
    const p = soldier.person,
      local = this.slot(slot);
    if (
      !a.mission ||
      a.state === 'wreck' ||
      slot < 0 ||
      slot >= 100 ||
      !Number.isInteger(slot) ||
      this.people.has(p.id) ||
      p.carrier ||
      p.fatal ||
      ['incapacitated', 'falling'].includes(p.state) ||
      p.p.distanceTo(this.point(a, local)) > 0.05 ||
      Math.abs((this.support(a, local) ?? Infinity) - local.y) > 0.04 ||
      [...this.people.values()].some((x) => x.aircraft === a && x.slot === slot && x.state !== 'departed')
    )
      return false;
    this.aviation.population.civil?.cancelTask(p, 'Original soldier at an assigned cargo-hold position');
    p.carrier = a.id;
    p.controlled = p.exposedSeat = true;
    p.hidden = false;
    p.state = 'wait';
    p.seatHeight = p.seatBlend = undefined;
    soldier.state = 'aboard';
    soldier.seat = slot;
    soldier.vehicle = undefined;
    this.people.set(p.id, { soldier, aircraft: a, slot, local, state: 'aboard', path: [], trail: [] });
    return true;
  }
  beginDrop(a: Aircraft, landings: ReadonlyMap<string, Vec>): boolean {
    if (a.state === 'wreck' || this.drops.has(a) || a.p.y < 20) return false;
    const queue = [...this.people.values()].filter(
      (o) => o.aircraft === a && o.state === 'aboard' && landings.has(o.soldier.id),
    );
    for (const o of queue) o.landing = landings.get(o.soldier.id);
    queue.sort((a, b) => b.local.z - a.local.z || Math.abs(a.local.x) - Math.abs(b.local.x));
    this.drops.set(a, { queue, next: this.aviation.time });
    return !!queue.length;
  }
  cancel(a?: Aircraft): void {
    for (const [aircraft] of this.drops) if (!a || a === aircraft) this.drops.delete(aircraft);
    for (const o of this.people.values())
      if ((!a || o.aircraft === a) && o.state === 'leaving') {
        o.state = 'returning';
        o.path = o.trail.slice().reverse();
        o.trail = [];
        o.landing = undefined;
      }
  }
  occupied(a: Aircraft): boolean {
    return [...this.people.values()].some(
      (o) => o.aircraft === a && !['departed', 'casualty'].includes(o.state),
    );
  }
  manoeuvreReady(a: Aircraft): boolean {
    return (
      this.aviation.time >= (this.exitClearanceUntil.get(a) ?? 0) &&
      ![...this.people.values()].some(
        (o) => o.aircraft === a && (o.state === 'leaving' || o.state === 'returning'),
      )
    );
  }
  private release(o: Occupant): void {
    const p = o.soldier.person;
    o.state = 'casualty';
    o.path = [];
    p.carrier = undefined;
    p.controlled = p.hidden = p.exposedSeat = false;
    if (p.state !== 'incapacitated') this.aviation.population.incapacitate(p, p.p, 1.8);
    p.velocity.add(o.aircraft.velocity);
    o.soldier.state = 'incapacitated';
  }
  private clearStep(o: Occupant, next: THREE.Vector3): boolean {
    for (const other of this.people.values()) {
      if (other === o || other.aircraft !== o.aircraft || ['departed', 'casualty'].includes(other.state))
        continue;
      const before = Math.hypot(o.local.x - other.local.x, o.local.z - other.local.z),
        after = Math.hypot(next.x - other.local.x, next.z - other.local.z);
      if (after < 0.49 && after <= before + 0.001) return false;
    }
    const a = o.aircraft,
      q = a.cluster.body.rotation(),
      from = this.point(a, { x: o.local.x, y: o.local.y + 0.86, z: o.local.z }, true),
      move = next.clone().sub(o.local).applyQuaternion(q);
    return !this.aviation.physics.world.castShape(from, q, move, this.capsule, 0.015, 1, false);
  }
  private stableExit(a: Aircraft): boolean {
    if (a.velocity.lengthSq() < 0.01) return true;
    const upright = UP.clone().applyQuaternion(a.q).dot(UP),
      aligned = new THREE.Vector3(0, 0, -1).applyQuaternion(a.q).dot(a.velocity.clone().normalize()),
      remaining = a.route[0]?.point.distanceTo(a.p) ?? 0,
      rotationChange = 1 - Math.abs(a.q.dot(new THREE.Quaternion().copy(a.cluster.body.rotation())));
    // A few degrees of remaining flare can lift the long rear hatch by tens
    // of centimetres per tick. Require completed rotation, not just a broadly
    // horizontal flight direction, before the translation-only exit sweep.
    return (
      upright > 0.9999999 &&
      aligned > 0.9999999 &&
      rotationChange < 1e-7 &&
      Math.abs(a.velocity.y) < 0.05 &&
      remaining > a.velocity.length() * 1.2
    );
  }
  update(dt: number): void {
    for (const o of this.people.values()) {
      if (['departed', 'casualty'].includes(o.state)) continue;
      const a = o.aircraft,
        p = o.soldier.person;
      p.previous.copy(p.p);
      p.p.copy(this.point(a, o.local));
      p.yaw = new THREE.Euler().setFromQuaternion(a.q, 'YXZ').y;
      if (
        a.state === 'wreck' ||
        !a.cluster.body.isValid() ||
        p.fatal ||
        ['incapacitated', 'falling'].includes(p.state)
      ) {
        this.release(o);
        continue;
      }
      if (o.state === 'aboard') {
        p.state = a.pilots?.includes(p.id) ? 'operating aircraft' : 'wait';
        continue;
      }
      const goal = o.path[0];
      if (!goal) {
        o.state = 'aboard';
        p.state = 'wait';
        continue;
      }
      if (o.local.distanceTo(goal) < 0.025) {
        o.trail.push(goal);
        o.path.shift();
        continue;
      }
      const move = goal.clone().sub(o.local).setY(0),
        span = move.length(),
        next = o.local.clone().addScaledVector(move.normalize(), Math.min(span, dt * p.speed));
      const support = this.support(a, next);
      if (support === undefined) {
        // A small upward step clears the real floor lip before gravity lowers
        // the capsule. Horizontal motion alone struck the lip after four ticks.
        const departure = new THREE.Vector3(0, 1.5, 3.5).applyQuaternion(a.q).add(a.velocity);
        if (
          o.state === 'leaving' &&
          o.landing &&
          o.local.z > 17.7 &&
          this.stableExit(a) &&
          this.clearStep(o, next) &&
          this.onJump(o.soldier, o.landing, departure, 0.7, a)
        ) {
          o.state = 'departed';
          this.exitClearanceUntil.set(a, this.aviation.time + 1.5);
          o.path = [];
        } else p.state = 'wait';
        continue;
      }
      if (Math.abs(support - o.local.y) > 0.25) {
        p.state = 'wait';
        continue;
      }
      next.y = support;
      if (this.clearStep(o, next)) {
        o.local.copy(next);
        p.p.copy(this.point(a, next));
        p.state = 'walk';
        p.yaw = new THREE.Euler().setFromQuaternion(a.q, 'YXZ').y + Math.atan2(-move.x, -move.z);
      } else p.state = 'wait';
    }
    for (const [a, d] of this.drops) {
      if (a.state === 'wreck') {
        this.drops.delete(a);
        continue;
      }
      const active = [...this.people.values()].filter(
        (o) => o.aircraft === a && o.state === 'leaving',
      ).length;
      if (active >= 8 || this.aviation.time < d.next) continue;
      const o = d.queue.shift();
      if (!o) {
        if (!active) this.drops.delete(a);
        continue;
      }
      if (o.state !== 'aboard') continue;
      const front = o.local.z + (o.slot < 2 ? 0.6 : -0.65),
        aisle = o.local.x < 0 ? -0.3 : 0.3;
      o.trail = [o.local.clone()];
      o.path = [
        new THREE.Vector3(o.local.x, 3.95, front),
        new THREE.Vector3(aisle, 3.95, front),
        new THREE.Vector3(aisle, 3.95, 18.5),
      ];
      o.state = 'leaving';
      d.next =
        this.aviation.time + (o.soldier.person.elite ? 0.2 : o.soldier.faction === 'hostile' ? 0.275 : 0.55);
    }
  }
  inspect(a?: Aircraft): unknown {
    return [...this.people.values()]
      .filter((o) => !a || o.aircraft === a)
      .map((o) => ({
        id: o.soldier.id,
        aircraft: o.aircraft.id,
        slot: o.slot,
        state: o.state,
        local: o.local.toArray(),
        position: o.soldier.person.p.toArray(),
        next: o.path[0]?.toArray(),
      }));
  }
  forget(a: Aircraft): void {
    if (this.occupied(a)) return;
    this.drops.delete(a);
    this.exitClearanceUntil.delete(a);
    for (const [id, o] of this.people) if (o.aircraft === a) this.people.delete(id);
  }
}
